import type {
  CseBudgetType, SynthesisOperationTypeSummary, SynthesisResult, SynthesisTotals,
} from "../types/accounting.types";

type ApiRow = {
  operation_type_id: number | null
  operation_type_name: string
  budget_type: CseBudgetType | null
  nature: "expense" | "revenue" | "unclassified"
  total_debit: number
  total_credit: number
  transaction_count: number
  realized: number
  previous_realized: number
  budget: number | null
  remaining: number | null
};
type ApiSection = {
  rows: ApiRow[]
  realized: number
  previous_realized: number
  budget: number | null
  previous_budget: number | null
  remaining: number | null
  transaction_count: number
};
export type NetBudgetSynthesisApi = {
  expenses: ApiSection
  revenues: ApiSection
  unclassified: ApiSection
  balance: {
    realized: number
    previous_realized: number
    budget: number | null
    previous_budget: number | null
  }
  monthly_breakdown: Array<{ month: number, expenses: number, revenues: number }>
  previous_monthly_breakdown: Array<{ month: number, expenses: number, revenues: number }>
};

const mapSection = (section: ApiSection) => {
  const rows: SynthesisOperationTypeSummary[] = section.rows.map((row) => ({
    operationTypeId: row.operation_type_id,
    operationTypeName: row.operation_type_name,
    budgetType: row.budget_type,
    nature: row.nature,
    totalDebit: row.total_debit,
    totalCredit: row.total_credit,
    balance: row.total_credit - row.total_debit,
    amount: row.total_debit - row.total_credit,
    consumed: row.realized,
    previousConsumed: row.previous_realized,
    budget: row.budget,
    delta: row.remaining,
    transactionCount: row.transaction_count,
  }));
  const totalDebit = rows.reduce((sum, row) => sum + row.totalDebit, 0);
  const totalCredit = rows.reduce((sum, row) => sum + row.totalCredit, 0);
  const totals: SynthesisTotals = {
    totalDebit, totalCredit, balance: totalCredit - totalDebit, amount: totalDebit - totalCredit,
    consumed: section.realized, previousConsumed: section.previous_realized,
    budget: section.budget, previousBudget: section.previous_budget,
    delta: section.remaining, transactionCount: section.transaction_count,
  };
  return { rows, totals };
};

// Keep existing chart/table interfaces, with their values now sourced from
// category-aware net realized amounts. Posting debit/credit remain available.
export const applyNetBudgetSynthesis = (legacy: SynthesisResult, data?: NetBudgetSynthesisApi): SynthesisResult => {
  if (!data) return legacy;
  const expenses = mapSection(data.expenses);
  const revenues = mapSection(data.revenues);
  const unclassified = mapSection(data.unclassified);
  return {
    ...legacy,
    sections: {
      expenses, revenues, unclassified, balance: { realized: data.balance.realized, budget: data.balance.budget },
    },
    byOperationType: [...expenses.rows, ...revenues.rows, ...unclassified.rows],
    totals: {
      ...legacy.totals,
      consumed: expenses.totals.consumed,
      budget: expenses.totals.budget,
      delta: expenses.totals.delta,
      previousConsumed: expenses.totals.previousConsumed,
      previousBudget: expenses.totals.previousBudget,
      balance: data.balance.realized,
    },
    monthlyBreakdown: data.monthly_breakdown.map((row) => ({ month: row.month, totalDebit: row.expenses, totalCredit: row.revenues })),
    previousExercice: legacy.previousExercice
      ? {
        ...legacy.previousExercice,
        consumed: data.expenses.previous_realized,
        budget: data.expenses.previous_budget,
        monthlyConsumed: data.previous_monthly_breakdown.map((row) => ({ month: row.month, consumed: row.expenses })),
      }
      : null,
  };
};
